import { useInfiniteQuery, useQuery } from 'react-query';
import { FilterID } from 'rwl-ui';
import { PluginWorkSite } from 'rwl-shared/dist/models';
import {
    HAS_PHOTOS,
    HAS_REVIEW,
    NON_TAG_PREFIX,
} from '../ConfigFetcher/transformers';
import { useAPIRoute } from '../../hooks/useAPIRoute';

type UseFetchJobsParams = {
    accountId: string;
    filters: FilterID[];
    enabled: boolean;
    bounds?: string | undefined;
    showcaseOnly?: boolean;
    pageSize?: number;
    useLabels?: boolean;
};

type mapAPIResponse = {
    count: number;
    workSites: PluginWorkSite[];
};

export function transformFilter(filters: FilterID[]): string {
    const labels = filters.filter(
        (filter) => !filter.startsWith(NON_TAG_PREFIX)
    );
    const onlyMedia = filters.includes(HAS_PHOTOS);
    const onlyReview = filters.includes(HAS_REVIEW);
    if (!labels.length && !onlyMedia && !onlyReview) {
        return '{}';
    }
    return JSON.stringify({
        tags: labels,
        needsMedia: onlyMedia,
        needsReview: onlyReview,
    });
}

export const useFetchJobs = ({
    accountId,
    filters,
    enabled,
    bounds,
    pageSize = 50,
    showcaseOnly,
}: UseFetchJobsParams) => {
    const contentFilters = transformFilter(filters || []);
    const url = useAPIRoute('map');

    return useQuery<mapAPIResponse>(
        ['jobs', accountId, contentFilters, bounds, pageSize, showcaseOnly],
        async () => {
            const result = await fetch(
                `${url}?` +
                    new URLSearchParams({
                        accountId: accountId,
                        contentFilters,
                        box: bounds ?? '',
                        limit: pageSize?.toString() ?? '',
                        showcaseOnly: showcaseOnly?.toString() ?? '',
                    }),
                {
                    method: 'GET',
                }
            );
            return result.json();
        },
        {
            // CoPilot suggested these, lets see if we like them
            enabled,
            staleTime: 1000 * 60 * 60 * 24, // 24 hours
            // With map bounds, we'll rarely have a cache hit, but we don't want cache to bloat into a memory leak
            cacheTime: 1000 * 10,
        }
    );
};

export const useFetchInfiniteJobs = ({
    accountId,
    filters,
    enabled,
    bounds,
    pageSize = 50,
    showcaseOnly,
}: UseFetchJobsParams) => {
    const contentFilters = transformFilter(filters || []);
    const url = useAPIRoute('map');

    return useInfiniteQuery<mapAPIResponse>(
        [
            'jobs',
            accountId,
            'infinite',
            contentFilters,
            bounds,
            pageSize,
            showcaseOnly,
        ],
        async ({ pageParam = 0 }) => {
            const result = await fetch(
                `${url}?` +
                    new URLSearchParams({
                        accountId: accountId,
                        contentFilters,
                        box: bounds ?? '',
                        limit: pageSize?.toString() ?? '',
                        skip: pageParam.toString(),
                        showcaseOnly: showcaseOnly?.toString() ?? '',
                    }),
                {
                    method: 'GET',
                }
            );

            return result.json();
        },
        {
            getNextPageParam: (lastPage, pages) => {
                if (lastPage.workSites?.length) {
                    return pages.length * pageSize;
                }
                return undefined;
            },
            enabled,
            staleTime: 1000 * 60 * 60 * 24, // 24 hours
            // With map bounds, we'll rarely have a cache hit, but we don't want cache to bloat into a memory leak
            cacheTime: 1000 * 10, // With map bounds, we'll rarely have a cache hit, but we don't want cache to bloat
        }
    );
};
